import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Returns the clamped start timestamp (ms) for event count queries.
 *
 * When a mid-cycle plan conversion occurs, the billing service records the
 * conversion moment as `paidUsageStartAt` on the tenant.  We use that value
 * only when it falls strictly after the current calendar-month start (UTC);
 * otherwise we fall back to the month start so the count covers the full
 * billing month.
 *
 * When the anchor is used, it is ceiled to the next hour boundary so that the
 * partial pre-upgrade hour bucket is excluded from counts (the usage API
 * buckets events by the hour). A timestamp already exactly on an hour boundary
 * is left unchanged (mathematical ceiling).
 *
 * Note: monthStart is evaluated at call time — stable until the next
 * tenantInfo refresh triggers a re-evaluation.
 */
export function resolveUsageWindowStart(paidUsageStartAt: string | undefined): number {
  const monthStart = dayjs().utc().startOf('month').valueOf();

  if (!paidUsageStartAt) {
    return monthStart;
  }

  // Parse as UTC explicitly (dayjs.utc) so a timezone-less server string is not
  // shifted by the host machine's offset.
  const anchor = dayjs.utc(paidUsageStartAt);

  if (anchor.valueOf() <= monthStart) {
    return monthStart;
  }

  // Usage API buckets by the hour — ceil the upgrade anchor up to the next hour
  // boundary so the partial pre-upgrade hour is excluded. Exact-hour timestamps
  // are left unchanged (mathematical ceiling). A sub-hour anchor in the final
  // hour of the month ceils into the next month's first instant; that is an
  // intentional, accepted edge case (the API returns the empty post-upgrade window).
  const startOfHour = anchor.startOf('hour');
  const ceiledToNextHour = startOfHour.isSame(anchor) ? startOfHour : startOfHour.add(1, 'hour');

  return ceiledToNextHour.valueOf();
}

/**
 * Returns true only when the given ISO string parses to a moment strictly
 * after the current UTC calendar-month start — i.e. a mid-cycle window is
 * active.  Consistent with the clamp logic in resolveUsageWindowStart.
 *
 * Note: monthStart is evaluated at call time — stable until the next
 * tenantInfo refresh triggers a re-evaluation.
 */
export function isUsageWindowMidCycle(isoString: string | undefined): isoString is string {
  if (!isoString) {
    return false;
  }

  const monthStart = dayjs().utc().startOf('month').valueOf();
  const analysisStart = dayjs.utc(isoString).valueOf();

  return analysisStart > monthStart;
}
